import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, MessageCircle, ArrowRight, X } from "lucide-react";
import { BRAND, IMAGES, ADVISORS } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  "Ahorro",
  "Inversión",
  "Protección / Seguros",
  "Plan de retiro",
  "Otro",
];

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: INTERESTS[0],
    message: "",
    advisor: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Listen for advisor pre-selection from other components (Advisor dialog, ScheduleCTA, etc.)
  useEffect(() => {
    const applyAdvisor = (name) => {
      if (!name) return;
      setForm((f) => ({ ...f, advisor: name }));
    };
    try {
      const stored = sessionStorage.getItem("emb:selectedAdvisor");
      if (stored) applyAdvisor(stored);
    } catch (_) {}
    const handler = (e) => applyAdvisor(e?.detail?.advisorName);
    window.addEventListener("emb:select-advisor", handler);
    return () => window.removeEventListener("emb:select-advisor", handler);
  }, []);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const clearAdvisor = () => {
    setForm((f) => ({ ...f, advisor: "" }));
    try {
      sessionStorage.removeItem("emb:selectedAdvisor");
    } catch (_) {}
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Por favor completa nombre, email y teléfono.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Solicitud enviada. Nos pondremos en contacto contigo en breve.");
      setForm({
        name: "",
        email: "",
        phone: "",
        interest: INTERESTS[0],
        message: "",
        advisor: "",
      });
      try {
        sessionStorage.removeItem("emb:selectedAdvisor");
      } catch (_) {}
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "No se pudo enviar la solicitud. Intenta de nuevo.";
      toast.error(typeof detail === "string" ? detail : "Error al enviar");
    } finally {
      setSubmitting(false);
    }
  };

  const wa = `https://wa.me/${BRAND.whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent(
    "Hola EMB Consulting Group, me gustaría agendar una asesoría."
  )}`;

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="relative bg-[#0B0410] text-[#F9F6F0] py-24 lg:py-32 border-t border-white/5 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
        {/* LEFT — Info */}
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#E6D5B8]" />
            <span className="text-[11px] tracking-luxury uppercase text-[#E6D5B8] font-medium">
              Contacto
            </span>
          </div>
          <h2
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.08]"
            data-testid="contact-headline"
          >
            Empecemos con una{" "}
            <span className="italic text-[#E6D5B8]">conversación</span>.
          </h2>
          <p className="mt-6 text-base lg:text-lg text-[#F9F6F0]/70 font-light max-w-md">
            La primera sesión es sin costo y sin compromiso. Cuéntanos qué te
            preocupa o qué quieres lograr.
          </p>

          <div className="mt-12 space-y-6">
            <a
              href={`tel:${BRAND.phone}`}
              data-testid="contact-phone"
              className="flex items-center gap-4 group"
            >
              <div className="w-12 h-12 flex items-center justify-center border border-[#3D1565] rounded-sm group-hover:bg-[#3D1565] transition-colors">
                <Phone size={18} className="text-[#E6D5B8]" />
              </div>
              <div>
                <div className="text-[10px] tracking-luxury uppercase text-[#A397B4]">
                  Teléfono
                </div>
                <div className="text-base text-[#F9F6F0] group-hover:text-[#E6D5B8] transition-colors">
                  {BRAND.phone}
                </div>
              </div>
            </a>

            <a
              href={`mailto:${BRAND.email}`}
              data-testid="contact-email"
              className="flex items-center gap-4 group"
            >
              <div className="w-12 h-12 flex items-center justify-center border border-[#3D1565] rounded-sm group-hover:bg-[#3D1565] transition-colors">
                <Mail size={18} className="text-[#E6D5B8]" />
              </div>
              <div>
                <div className="text-[10px] tracking-luxury uppercase text-[#A397B4]">
                  Correo
                </div>
                <div className="text-base text-[#F9F6F0] group-hover:text-[#E6D5B8] transition-colors">
                  {BRAND.email}
                </div>
              </div>
            </a>

            <a
              href={wa}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp"
              className="flex items-center gap-4 group"
            >
              <div className="w-12 h-12 flex items-center justify-center border border-[#3D1565] rounded-sm group-hover:bg-[#3D1565] transition-colors">
                <MessageCircle size={18} className="text-[#E6D5B8]" />
              </div>
              <div>
                <div className="text-[10px] tracking-luxury uppercase text-[#A397B4]">
                  WhatsApp directo
                </div>
                <div className="text-base text-[#F9F6F0] group-hover:text-[#E6D5B8] transition-colors">
                  Conversemos ahora
                </div>
              </div>
            </a>

            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center border border-[#3D1565] rounded-sm">
                <MapPin size={18} className="text-[#E6D5B8]" />
              </div>
              <div>
                <div className="text-[10px] tracking-luxury uppercase text-[#A397B4]">
                  Ubicación
                </div>
                <div className="text-base text-[#F9F6F0]">{BRAND.address}</div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT — Form */}
        <div className="lg:col-span-7">
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="bg-[#150A1F] border border-[#2A0A4A] rounded-sm p-8 lg:p-10 space-y-6"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="text-[11px] tracking-luxury uppercase text-[#A397B4] mb-2 block">
                  Nombre completo
                </label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={setField("name")}
                  data-testid="contact-input-name"
                  className="w-full bg-[#0B0410] border border-[#2A0A4A] rounded-sm px-4 py-3 text-[#F9F6F0] focus:outline-none focus:border-[#E6D5B8] transition-colors"
                  placeholder="Tu nombre"
                />
              </div>
              <div>
                <label className="text-[11px] tracking-luxury uppercase text-[#A397B4] mb-2 block">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={setField("email")}
                  data-testid="contact-input-email"
                  className="w-full bg-[#0B0410] border border-[#2A0A4A] rounded-sm px-4 py-3 text-[#F9F6F0] focus:outline-none focus:border-[#E6D5B8] transition-colors"
                  placeholder="tu@correo.com"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="text-[11px] tracking-luxury uppercase text-[#A397B4] mb-2 block">
                  Teléfono
                </label>
                <input
                  type="tel"
                  required
                  value={form.phone}
                  onChange={setField("phone")}
                  data-testid="contact-input-phone"
                  className="w-full bg-[#0B0410] border border-[#2A0A4A] rounded-sm px-4 py-3 text-[#F9F6F0] focus:outline-none focus:border-[#E6D5B8] transition-colors"
                  placeholder="+52 ..."
                />
              </div>
              <div>
                <label className="text-[11px] tracking-luxury uppercase text-[#A397B4] mb-2 block">
                  Interés
                </label>
                <select
                  value={form.interest}
                  onChange={setField("interest")}
                  data-testid="contact-select-interest"
                  className="w-full bg-[#0B0410] border border-[#2A0A4A] rounded-sm px-4 py-3 text-[#F9F6F0] focus:outline-none focus:border-[#E6D5B8] transition-colors appearance-none"
                >
                  {INTERESTS.map((it) => (
                    <option key={it} value={it}>
                      {it}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="text-[11px] tracking-luxury uppercase text-[#A397B4] mb-2 block">
                Asesor preferido (opcional)
              </label>
              {form.advisor ? (
                <div
                  data-testid="contact-selected-advisor"
                  className="flex items-center justify-between gap-3 bg-[#3D1565]/30 border border-[#E6D5B8]/40 rounded-sm px-4 py-3"
                >
                  <span className="text-[#F9F6F0] text-sm">
                    Asesor seleccionado:{" "}
                    <span className="text-[#E6D5B8] font-medium">{form.advisor}</span>
                  </span>
                  <button
                    type="button"
                    onClick={clearAdvisor}
                    data-testid="contact-clear-advisor"
                    aria-label="Quitar asesor seleccionado"
                    className="text-[#A397B4] hover:text-[#E6D5B8] transition-colors"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <select
                  value={form.advisor}
                  onChange={setField("advisor")}
                  data-testid="contact-input-advisor"
                  className="w-full bg-[#0B0410] border border-[#2A0A4A] rounded-sm px-4 py-3 text-[#F9F6F0] focus:outline-none focus:border-[#E6D5B8] transition-colors"
                >
                  <option value="">— Sin preferencia —</option>
                  {ADVISORS.map((a) => (
                    <option key={a.name} value={a.name}>
                      {a.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label className="text-[11px] tracking-luxury uppercase text-[#A397B4] mb-2 block">
                Mensaje (opcional)
              </label>
              <textarea
                rows={4}
                value={form.message}
                onChange={setField("message")}
                data-testid="contact-input-message"
                className="w-full bg-[#0B0410] border border-[#2A0A4A] rounded-sm px-4 py-3 text-[#F9F6F0] focus:outline-none focus:border-[#E6D5B8] transition-colors resize-none"
                placeholder="Cuéntanos brevemente tu situación u objetivo financiero."
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="group w-full sm:w-auto bg-[#3D1565] hover:bg-[#4a1c79] disabled:opacity-60 disabled:cursor-not-allowed text-white px-8 py-4 rounded-sm transition-all duration-300 hover:-translate-y-0.5 flex items-center justify-center gap-2"
            >
              <span className="text-sm tracking-wider uppercase font-medium">
                {submitting ? "Enviando..." : "Enviar solicitud"}
              </span>
              <ArrowRight
                size={16}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </button>

            <p className="text-xs text-[#A397B4]/80 leading-relaxed">
              Al enviar este formulario aceptas que EMB Consulting Group te
              contacte para fines de asesoría. No compartimos tus datos con
              terceros.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
